import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { useAuth } from '@/contexts/AuthContext';
import { organogramasService } from '@/services/api';
import { Organograma as OrganogramaType, OrganogramaTipo } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { organogramaTemplates } from '@/components/organograma/organogramaDemoData';
import { GerarOrganogramaDialog } from '@/components/organograma/GerarOrganogramaDialog';
import { TipoOrganogramaSelect, organogramaTipoLabel } from '@/components/organograma/TipoOrganogramaSelect';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AtrelarProjetoDialog } from '@/components/shared/AtrelarProjetoDialog';
import { projetotService } from '@/services/projetotApi';
import type { ProjetoT } from '@/types';
import {
  Archive,
  Boxes,
  Clock,
  ChevronRight,
  Copy,
  Edit,
  Link,
  Link2,
  Loader2,
  MoreHorizontal,
  Network,
  Plus,
  Search,
  Share2,
  Star,
  StarOff,
  Trash2,
  UserRound,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';

type OrganogramaCreateFormState = {
  nome: string;
  descricao: string;
  tipo: OrganogramaTipo;
  status: OrganogramaType['status'];
};

const initialOrganogramaForm: OrganogramaCreateFormState = {
  nome: '',
  descricao: '',
  tipo: 'departamento',
  status: 'rascunho',
};

export default function Organograma() {
  const navigate = useNavigate();
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [organogramas, setOrganogramas] = useState<OrganogramaType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOrganogramaDialogOpen, setIsOrganogramaDialogOpen] = useState(false);
  const [isGerarDialogOpen, setIsGerarDialogOpen] = useState(false);
  const [editingOrganograma, setEditingOrganograma] = useState<OrganogramaType | null>(null);
  const [archiveOrganogramaId, setArchiveOrganogramaId] = useState<string | null>(null);
  const [deleteOrganogramaId, setDeleteOrganogramaId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [organogramaForm, setOrganogramaForm] = useState<OrganogramaCreateFormState>(initialOrganogramaForm);
  /** Só mostra o erro de nome vazio depois de uma tentativa de salvar — não de cara ao abrir o diálogo. */
  const [tentouSalvarOrganograma, setTentouSalvarOrganograma] = useState(false);
  /** `null` = começar em branco. Só vale na criação; ao editar, o canvas já existe. */
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [projetosT, setProjetosT] = useState<ProjetoT[]>([]);
  const [atrelarTarget, setAtrelarTarget] = useState<OrganogramaType | null>(null);

  const isSuperAdmin = user?.tipo === 'superadmin';
  const canEdit = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const effectiveEmpresaId = isSuperAdmin ? (empresaFiltroId || '') : empresaAtiva?.id || user?.empresa_id || '';

  // `?gerar=1` (vindo do cadastro inteligente) abre direto o gerador a partir dos cadastros.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('gerar') !== '1' || !effectiveEmpresaId) return;
    setIsGerarDialogOpen(true);
    const semParametro = new URLSearchParams(searchParams);
    semParametro.delete('gerar');
    setSearchParams(semParametro, { replace: true });
  }, [searchParams, setSearchParams, effectiveEmpresaId]);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const filteredOrganogramas = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();

    return organogramas.filter((organograma) => {
      const matchesSearch = !normalizedSearch
        || organograma.nome.toLowerCase().includes(normalizedSearch)
        || (organograma.descricao || '').toLowerCase().includes(normalizedSearch);
      const matchesStatus = statusFilter === 'todos' || organograma.status === statusFilter;
      const matchesTipo = tipoFilter === 'todos' || organograma.tipo === tipoFilter;

      return matchesSearch && matchesStatus && matchesTipo;
    });
  }, [organogramas, deferredSearchTerm, statusFilter, tipoFilter]);

  const loadOrganogramas = useCallback(async () => {
    if (!effectiveEmpresaId) return;

    try {
      setIsLoading(true);
      const result = await organogramasService.getAll(isSuperAdmin ? effectiveEmpresaId : undefined);
      if (result.success) setOrganogramas(result.data);
      else toast.error(result.error || 'Erro ao carregar organogramas');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveEmpresaId, isSuperAdmin]);

  useEffect(() => {
    loadOrganogramas();
  }, [loadOrganogramas]);

  useEffect(() => {
    if (!effectiveEmpresaId) {
      setProjetosT([]);
      return;
    }
    projetotService.getAll(undefined, isSuperAdmin ? effectiveEmpresaId : undefined).then((result) => {
      if (result.success) setProjetosT(result.data || []);
    });
  }, [effectiveEmpresaId, isSuperAdmin]);

  const handleAtrelarProjeto = async (projetotId: string | null) => {
    if (!atrelarTarget) return;
    const result = await organogramasService.update(atrelarTarget.id, { projetot_id: projetotId });
    if (result.success) {
      toast.success('Vínculo atualizado');
      loadOrganogramas();
    } else {
      toast.error(result.error || 'Erro ao atrelar projeto');
    }
  };

  const openCreateOrganograma = () => {
    setEditingOrganograma(null);
    setOrganogramaForm(initialOrganogramaForm);
    setTemplateKey(null);
    setTentouSalvarOrganograma(false);
    setIsOrganogramaDialogOpen(true);
  };

  const openEditOrganograma = (organograma: OrganogramaType) => {
    setEditingOrganograma(organograma);
    setTemplateKey(null);
    setTentouSalvarOrganograma(false);
    setOrganogramaForm({
      nome: organograma.nome,
      descricao: organograma.descricao || '',
      tipo: organograma.tipo,
      status: organograma.status,
    });
    setIsOrganogramaDialogOpen(true);
  };

  const handleSaveOrganograma = async () => {
    setTentouSalvarOrganograma(true);

    if (!effectiveEmpresaId) {
      toast.error('Selecione uma empresa');
      return;
    }

    if (!organogramaForm.nome.trim()) {
      toast.error('Informe o nome do organograma');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        nome: organogramaForm.nome,
        descricao: organogramaForm.descricao || null,
        tipo: organogramaForm.tipo,
        status: organogramaForm.status,
        visibilidade: 'empresa' as const,
      };
      const result = editingOrganograma
        ? await organogramasService.update(editingOrganograma.id, payload)
        : await organogramasService.create({
            empresa_id: isSuperAdmin ? effectiveEmpresaId : undefined,
            ...payload,
          });

      if (result.success) {
        toast.success(editingOrganograma ? 'Organograma atualizado' : 'Organograma criado');
        setIsOrganogramaDialogOpen(false);
        setEditingOrganograma(null);
        if (editingOrganograma) await loadOrganogramas();
        else navigate(`/organograma/${result.data.id}${templateKey ? `?template=${templateKey}` : ''}`);
      } else {
        toast.error(result.error || 'Erro ao salvar organograma');
      }
    } finally {
      setIsSaving(false);
    }
  };

  /** Copia texto pra área de transferência com fallback seguro — a Clipboard API
   *  (`navigator.clipboard`) só existe em contexto seguro (HTTPS ou localhost).
   *  Acessando por IP/Tailscale via HTTP puro, `navigator.clipboard` é `undefined`,
   *  e chamar `.writeText` nele lança uma exceção síncrona — sem o try/catch aqui,
   *  isso interrompia a função no meio, deixando o toast de "carregando" travado
   *  pra sempre e o botão de ações travado até dar F5. */
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // cai pro fallback abaixo
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    } catch {
      return false;
    }
  };

  const handleSetPrincipal = async (organograma: OrganogramaType) => {
    setActionLoadingId(organograma.id);
    try {
      const result = await organogramasService.update(organograma.id, {
        is_principal: true,
        status: organograma.status === 'arquivado' ? 'ativo' : organograma.status,
      });

      if (result.success) {
        toast.success('Organograma definido como principal');
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao definir organograma principal');
      }
    } catch {
      toast.error('Erro ao definir organograma principal');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleUnsetPrincipal = async (organograma: OrganogramaType) => {
    setActionLoadingId(organograma.id);
    try {
      const result = await organogramasService.update(organograma.id, { is_principal: false });
      if (result.success) {
        toast.success('Organograma removido como principal');
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao remover organograma como principal');
      }
    } catch {
      toast.error('Erro ao remover organograma como principal');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDuplicateOrganograma = async (organograma: OrganogramaType) => {
    setActionLoadingId(organograma.id);
    const toastId = `duplicar-${organograma.id}`;
    toast.loading('Duplicando organograma...', { id: toastId });
    try {
      const result = await organogramasService.duplicate(organograma.id);
      if (result.success) {
        toast.success('Organograma duplicado — clique em "Abrir" para acessar a cópia', { id: toastId });
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao duplicar organograma', { id: toastId });
      }
    } catch {
      toast.error('Erro ao duplicar organograma', { id: toastId });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleArchiveOrganograma = async () => {
    if (!archiveOrganogramaId) return;
    const id = archiveOrganogramaId;
    setActionLoadingId(id);

    try {
      const result = await organogramasService.archive(id);
      if (result.success) {
        toast.success('Organograma arquivado');
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao arquivar organograma');
      }
    } catch {
      toast.error('Erro ao arquivar organograma');
    } finally {
      setArchiveOrganogramaId(null);
      setActionLoadingId(null);
    }
  };

  const handleShareOrganograma = async (organograma: OrganogramaType) => {
    setActionLoadingId(organograma.id);
    const toastId = `compartilhar-${organograma.id}`;
    toast.loading('Gerando link público...', { id: toastId });
    try {
      const result = await organogramasService.generateShareLink(organograma.id);
      if (result.success && result.data?.share_token) {
        const url = `${window.location.origin}/o/${result.data.share_token}`;
        const copied = await copyToClipboard(url);
        toast.success(copied ? 'Link público gerado e copiado' : 'Link público gerado', { id: toastId });
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao gerar link público', { id: toastId });
      }
    } catch {
      toast.error('Erro ao gerar link público', { id: toastId });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopyShareLink = async (organograma: OrganogramaType) => {
    if (!organograma.share_token) return;
    const url = `${window.location.origin}/o/${organograma.share_token}`;
    const copied = await copyToClipboard(url);
    if (copied) toast.success('Link público copiado para a área de transferência');
    else toast.error('Não foi possível copiar automaticamente — copie o link manualmente');
  };

  const handleRevokeOrganograma = async (organograma: OrganogramaType) => {
    setActionLoadingId(organograma.id);
    const toastId = `revogar-${organograma.id}`;
    toast.loading('Revogando link...', { id: toastId });
    try {
      const result = await organogramasService.revokeShareLink(organograma.id);
      if (result.success) {
        toast.success('Link público revogado', { id: toastId });
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao revogar link', { id: toastId });
      }
    } catch {
      toast.error('Erro ao revogar link', { id: toastId });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteOrganograma = async () => {
    if (!deleteOrganogramaId) return;
    const id = deleteOrganogramaId;
    setActionLoadingId(id);

    try {
      const result = await organogramasService.delete(id);
      if (result.success) {
        toast.success('Organograma excluído');
        await loadOrganogramas();
      } else {
        toast.error(result.error || 'Erro ao excluir organograma');
      }
    } catch {
      toast.error('Erro ao excluir organograma');
    } finally {
      setDeleteOrganogramaId(null);
      setActionLoadingId(null);
    }
  };

  const renderHeaderActions = () => (
    <div className="flex w-full flex-wrap gap-2 md:w-auto md:justify-end">
      {canEdit && (
        <Button onClick={openCreateOrganograma} disabled={!effectiveEmpresaId} data-tour="novo-organograma">
          <Plus className="mr-2 h-4 w-4" />
          Novo organograma
        </Button>
      )}
      {canEdit && (
        <Button variant="secondary" onClick={() => setIsGerarDialogOpen(true)} disabled={!effectiveEmpresaId} data-tour="gerar-organograma">
          <Wand2 className="mr-2 h-4 w-4" />
          Gerar a partir dos cadastros
        </Button>
      )}
    </div>
  );

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="overflow-hidden rounded-2xl border bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-5 text-white shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-blue-100">
                <Network className="h-3.5 w-3.5" />
                Central de estruturas
              </div>
              <h1 className="text-3xl font-bold tracking-tight">Organogramas</h1>
              <p className="mt-2 text-sm text-slate-300 md:text-base">
                Abra uma estrutura para navegar pelos ramos, consultar pessoas, departamentos e relações matriciais em uma visualização mais clara.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm sm:min-w-[360px]">
              <div className="rounded-xl border border-white/10 bg-white/10 p-3">
                <div className="text-xs text-blue-100">Total</div>
                <div className="text-2xl font-semibold">{organogramas.length}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/10 p-3">
                <div className="text-xs text-blue-100">Ativos</div>
                <div className="text-2xl font-semibold">{organogramas.filter((item) => item.status === 'ativo').length}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/10 p-3">
                <div className="text-xs text-blue-100">Itens</div>
                <div className="text-2xl font-semibold">{organogramas.reduce((total, item) => total + (item.total_nodes || 0), 0)}</div>
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-slate-400">Use os filtros para encontrar uma estrutura e clique em abrir para visualizar os dados.</p>
            {renderHeaderActions()}
          </div>
        </div>

        {isSuperAdmin && !effectiveEmpresaId ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
            <Network className="mb-3 h-10 w-10 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Organograma é por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
            </p>
          </div>
        ) : isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-56 w-full rounded-lg" />
            ))}
          </div>
        ) : organogramas.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
            <Network className="mb-3 h-10 w-10 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Nenhum organograma criado</h2>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {canEdit ? 'Crie um organograma vazio para comecar a montar a estrutura.' : 'A estrutura ainda nao foi cadastrada.'}
            </p>
            {canEdit && (
              <Button className="mt-4" onClick={openCreateOrganograma} disabled={!effectiveEmpresaId}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeiro organograma
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid gap-3 rounded-lg border bg-card p-3 md:grid-cols-[1fr_180px_180px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nome ou descrição"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="rascunho">Rascunho</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  <SelectItem value="oficial">Oficial</SelectItem>
                  <SelectItem value="planejamento">Planejamento</SelectItem>
                  <SelectItem value="departamento">Departamento</SelectItem>
                  <SelectItem value="projeto">Projeto</SelectItem>
                  <SelectItem value="customizado">Customizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filteredOrganogramas.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center">
                <h2 className="text-lg font-semibold">Nenhum resultado encontrado</h2>
                <p className="mt-1 text-sm text-muted-foreground">Ajuste a busca ou os filtros para ver outros organogramas.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredOrganogramas.map((organograma) => (
                  <Card
                    key={organograma.id}
                    className="group relative overflow-hidden border-border/80 bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
                  >
                    {organograma.share_token && (
                      <Badge variant="secondary" className="absolute right-2 top-2 z-10 gap-1 text-[10px] text-emerald-600">
                        <Link2 className="h-3 w-3" />
                        Público
                      </Badge>
                    )}
                    <CardContent className="space-y-4 p-5">
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 transition-transform group-hover:scale-105">
                          <Network className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h2 className="line-clamp-2 min-h-[2.8rem] break-words text-lg font-semibold leading-tight">{organograma.nome}</h2>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {organograma.is_principal && <Badge>Principal</Badge>}
                            <Badge variant="outline">{organogramaTipoLabel(organograma.tipo)}</Badge>
                            <StatusBadge status={organograma.status} />
                            {organograma.projetoT && (
                              <Badge
                                variant="outline"
                                className="gap-1 cursor-pointer hover:bg-accent"
                                onClick={(e) => { e.stopPropagation(); navigate(`/projetos/${organograma.projetoT!.id}`); }}
                              >
                                <Link className="h-3 w-3" />
                                {organograma.projetoT.titulo}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <p className="line-clamp-2 text-sm leading-5 text-muted-foreground">
                        {organograma.descricao || 'Sem descrição cadastrada.'}
                      </p>

                      <div className="flex flex-col gap-2 rounded-xl border bg-muted/20 px-3 py-2.5 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Boxes className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span><strong className="font-semibold text-foreground">{organograma.total_nodes || 0}</strong> itens</span>
                        </div>
                        {organograma.criador?.nome && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <UserRound className="h-3.5 w-3.5 shrink-0 text-primary" />
                            <span>Criado por <strong className="font-semibold text-foreground">{organograma.criador.nome}</strong></span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span>Atualizado em <strong className="font-semibold text-foreground">{formatDate(organograma.updatedAt)}</strong></span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 border-t pt-4">
                        <Button className="flex-1" size="sm" onClick={() => navigate(`/organograma/${organograma.id}`)}>
                          Abrir organograma
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" disabled={actionLoadingId === organograma.id} aria-label="Ações">
                                {actionLoadingId === organograma.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onSelect={() => openEditOrganograma(organograma)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              {organograma.is_principal ? (
                                <DropdownMenuItem onSelect={() => handleUnsetPrincipal(organograma)}>
                                  <StarOff className="mr-2 h-4 w-4" />
                                  Remover como principal
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onSelect={() => handleSetPrincipal(organograma)}>
                                  <Star className="mr-2 h-4 w-4" />
                                  Tornar principal
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onSelect={() => handleDuplicateOrganograma(organograma)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicar
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => setAtrelarTarget(organograma)}>
                                <Link className="mr-2 h-4 w-4" />
                                Atrelar a um projeto
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {organograma.share_token ? (
                                <>
                                  <DropdownMenuItem onSelect={() => handleCopyShareLink(organograma)}>
                                    <Link2 className="mr-2 h-4 w-4" />
                                    Copiar link público
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => handleRevokeOrganograma(organograma)}>
                                    <Share2 className="mr-2 h-4 w-4" />
                                    Revogar link
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem onSelect={() => handleShareOrganograma(organograma)}>
                                  <Share2 className="mr-2 h-4 w-4" />
                                  Compartilhar
                                </DropdownMenuItem>
                              )}
                              {organograma.status !== 'arquivado' && (
                                <DropdownMenuItem onSelect={() => setArchiveOrganogramaId(organograma.id)}>
                                  <Archive className="mr-2 h-4 w-4" />
                                  Arquivar
                                </DropdownMenuItem>
                              )}
                              {!organograma.is_principal && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onSelect={() => setDeleteOrganogramaId(organograma.id)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Excluir
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <GerarOrganogramaDialog
        open={isGerarDialogOpen}
        onOpenChange={setIsGerarDialogOpen}
        empresaId={isSuperAdmin ? effectiveEmpresaId : undefined}
      />

      <Dialog open={isOrganogramaDialogOpen} onOpenChange={setIsOrganogramaDialogOpen}>
        <DialogContent className={editingOrganograma ? 'sm:max-w-lg' : 'max-w-3xl'}>
          <DialogHeader>
            <DialogTitle>{editingOrganograma ? 'Editar organograma' : 'Novo organograma'}</DialogTitle>
            {!editingOrganograma && (
              <DialogDescription>Comece a partir de um modelo pronto ou em branco.</DialogDescription>
            )}
          </DialogHeader>
          {!editingOrganograma && (
            <div className="grid max-h-[40vh] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-3">
              <button
                type="button"
                onClick={() => setTemplateKey(null)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  templateKey === null ? 'border-primary bg-primary/5' : 'hover:border-primary/40'
                }`}
              >
                <div className="font-medium">Em branco</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Canvas vazio — você monta a estrutura do zero.
                </div>
              </button>
              {organogramaTemplates.map((template) => (
                <button
                  type="button"
                  key={template.key}
                  onClick={() => setTemplateKey(template.key)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    templateKey === template.key ? 'border-primary bg-primary/5' : 'hover:border-primary/40'
                  }`}
                >
                  <div className="font-medium">{template.nome}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{template.descricao}</div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {template.nodes.length} nós · {template.edges.length} conexões
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="organograma-nome">Nome *</Label>
              <Input
                id="organograma-nome"
                required
                aria-invalid={tentouSalvarOrganograma && !organogramaForm.nome.trim()}
                value={organogramaForm.nome}
                onChange={(event) => setOrganogramaForm({ ...organogramaForm, nome: event.target.value })}
                placeholder="Ex: Organograma oficial"
                className={
                  tentouSalvarOrganograma && !organogramaForm.nome.trim()
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              {tentouSalvarOrganograma && !organogramaForm.nome.trim() && (
                <p className="text-xs text-destructive">Informe o nome do organograma.</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="organograma-descricao">Descrição</Label>
              <Textarea
                id="organograma-descricao"
                value={organogramaForm.descricao}
                onChange={(event) => setOrganogramaForm({ ...organogramaForm, descricao: event.target.value })}
                placeholder="Ex: Estrutura atual da empresa"
              />
            </div>
            <div className="grid gap-2">
              <Label>Tipo</Label>
              <TipoOrganogramaSelect
                value={organogramaForm.tipo}
                onChange={(value) => setOrganogramaForm({ ...organogramaForm, tipo: value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={organogramaForm.status}
                onValueChange={(value: OrganogramaType['status']) => setOrganogramaForm({ ...organogramaForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rascunho">Rascunho</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOrganogramaDialogOpen(false);
                setEditingOrganograma(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveOrganograma} disabled={isSaving || !organogramaForm.nome.trim()}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingOrganograma ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveOrganogramaId} onOpenChange={(open) => !open && setArchiveOrganogramaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar organograma?</AlertDialogTitle>
            <AlertDialogDescription>
              Ele deixará de aparecer como estrutura ativa, mas continuará registrado para consulta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoadingId === archiveOrganogramaId}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveOrganograma} disabled={actionLoadingId === archiveOrganogramaId}>
              {actionLoadingId === archiveOrganogramaId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteOrganogramaId} onOpenChange={(open) => !open && setDeleteOrganogramaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir organograma?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove o organograma e todos os itens cadastrados nele.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoadingId === deleteOrganogramaId}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOrganograma} disabled={actionLoadingId === deleteOrganogramaId}>
              {actionLoadingId === deleteOrganogramaId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AtrelarProjetoDialog
        open={!!atrelarTarget}
        onOpenChange={(open) => { if (!open) setAtrelarTarget(null); }}
        projetos={projetosT}
        currentId={atrelarTarget?.projetot_id}
        onConfirm={handleAtrelarProjeto}
      />

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/organograma.png" titulo="Organograma" />
    </AppLayout>
  );
}

function formatDate(value?: string) {
  if (!value) return 'Sem registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem registro';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
